import React, { useState, useEffect } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { OnyxMascotFace } from "../../shared/OnyxMascot";

const BENEFITS = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: "Live in 5 minutes",
    desc: "No coding, no complex setup. Just connect and go.",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
      </svg>
    ),
    title: "BM, EN & Chinese",
    desc: "Native trilingual support for Malaysian businesses.",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
      </svg>
    ),
    title: "No credit card needed",
    desc: "Start free with 100 messages/month. Upgrade anytime.",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 002.25-2.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v2.25A2.25 2.25 0 006 10.5zm0 9.75h2.25A2.25 2.25 0 0010.5 18v-2.25a2.25 2.25 0 00-2.25-2.25H6a2.25 2.25 0 00-2.25 2.25V18A2.25 2.25 0 006 20.25zm9.75-9.75H18a2.25 2.25 0 002.25-2.25V6A2.25 2.25 0 0018 3.75h-2.25A2.25 2.25 0 0013.5 6v2.25a2.25 2.25 0 002.25 2.25z" />
      </svg>
    ),
    title: "Works everywhere",
    desc: "Embed on any website, WhatsApp, or Messenger.",
  },
];

const TRUST_STATS = [
  { value: "500+", label: "Businesses" },
  { value: "1M+", label: "Messages handled" },
  { value: "< 5 min", label: "Average setup" },
  { value: "99.9%", label: "Uptime" },
];

const TESTIMONIALS = [
  {
    quote: "We set it up over lunch and were live before dinner. Our customers love it.",
    name: "Siti Rahimah",
    company: "Maju Clinic, KL",
  },
  {
    quote: "Finally an AI chatbot that understands Bahasa Malaysia properly.",
    name: "Tan Wei Liang",
    company: "LiangMart Online",
  },
];

export function SignupPage() {
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Start Free — Onyx AI Chatbot for Your Business";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Create your free AI chatbot in 5 minutes. No coding, no credit card. Supports BM, EN & Chinese."
      );
    }

    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setReferralCode(ref);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/portal/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          name,
          companyName,
          ...(referralCode ? { referralCode } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Registration failed");
      localStorage.setItem("cf_token", data.token);
      window.location.href = "/portal";
    } catch (err: any) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />

      <main className="pt-16">
        {/* Hero split */}
        <section className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left — value proposition */}
          <div className="lg:pt-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#a078d0]/10 border border-[#a078d0]/20 text-xs text-[#c0a0e0] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a078d0] animate-pulse" />
              Free forever plan — no credit card
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 onyx-title">
              Start Your Free<br />
              <span className="onyx-gradient-text">AI Chatbot</span>
            </h1>
            <p className="text-lg text-[#b0a0c0] mb-10 leading-relaxed">
              Build an AI chatbot for your business in minutes. Speaks BM, English, and Chinese — no coding required.
            </p>

            {/* Benefits list */}
            <div className="space-y-5 mb-12">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#a078d0]/10 border border-[#a078d0]/20 flex items-center justify-center text-[#a078d0]">
                    {b.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{b.title}</p>
                    <p className="text-sm text-[#8a7a9a]">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-[#2a2040]">
              {TRUST_STATS.map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-2xl font-bold text-white onyx-title">{s.value}</p>
                  <p className="text-xs text-[#8a7a9a] mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — signup form */}
          <div className="onyx-card p-8 rounded-2xl border border-[#2a2040]" style={{ background: "linear-gradient(135deg, #14121e 0%, #1a1530 100%)" }}>
            {/* Mascot + heading */}
            <div className="flex items-center gap-3 mb-6">
              <OnyxMascotFace size={36} />
              <div>
                <h2 className="text-lg font-bold text-white">Create your free account</h2>
                <p className="text-xs text-[#8a7a9a]">Takes less than 5 minutes</p>
              </div>
            </div>

            {referralCode && (
              <div className="bg-[#a078d0]/10 border border-[#a078d0]/20 rounded-lg px-4 py-3 text-sm text-[#c0a0e0] mb-4">
                Referred by: <span className="font-mono font-bold">{referralCode}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#d0c0e8] mb-1.5">Your Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 bg-[#0a0a12] border border-[#2a2040] rounded-lg text-white text-sm focus:outline-none focus:border-[#a078d0] transition-colors placeholder-[#3a3060]"
                    placeholder="Ali Hassan"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#d0c0e8] mb-1.5">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 bg-[#0a0a12] border border-[#2a2040] rounded-lg text-white text-sm focus:outline-none focus:border-[#a078d0] transition-colors placeholder-[#3a3060]"
                    placeholder="My Business"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#d0c0e8] mb-1.5">Work Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 bg-[#0a0a12] border border-[#2a2040] rounded-lg text-white text-sm focus:outline-none focus:border-[#a078d0] transition-colors placeholder-[#3a3060]"
                  placeholder="ali@mybusiness.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#d0c0e8] mb-1.5">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-3 py-2.5 bg-[#0a0a12] border border-[#2a2040] rounded-lg text-white text-sm focus:outline-none focus:border-[#a078d0] transition-colors placeholder-[#3a3060]"
                  placeholder="Min. 8 characters"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#a078d0] hover:bg-[#8060b0] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors text-sm mt-2"
              >
                {loading ? "Creating your account…" : "Start for free →"}
              </button>
            </form>

            <p className="text-xs text-[#5a4a7a] text-center mt-4">
              By signing up you agree to our{" "}
              <a href="/terms" className="text-[#8a7a9a] hover:text-[#c0a0e0] transition-colors">Terms</a>{" "}
              and{" "}
              <a href="/privacy" className="text-[#8a7a9a] hover:text-[#c0a0e0] transition-colors">Privacy Policy</a>.
            </p>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#2a2040]" />
              <span className="text-xs text-[#3a3060]">or</span>
              <div className="flex-1 h-px bg-[#2a2040]" />
            </div>

            <p className="text-center text-sm text-[#5a4a7a]">
              Already have an account?{" "}
              <a href="/portal" className="text-[#c0a0e0] hover:text-[#d0b8f0] transition-colors font-medium">
                Sign in
              </a>
            </p>

            {/* Security badges */}
            <div className="flex items-center justify-center gap-5 mt-6 pt-5 border-t border-[#1a1530]">
              <div className="flex items-center gap-1.5 text-xs text-[#5a4a7a]">
                <svg className="w-3.5 h-3.5 text-[#a078d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
                SSL secured
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#5a4a7a]">
                <svg className="w-3.5 h-3.5 text-[#a078d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
                Data encrypted
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#5a4a7a]">
                <svg className="w-3.5 h-3.5 text-[#a078d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                </svg>
                No credit card
              </div>
            </div>
          </div>
        </section>

        {/* Social proof */}
        <section className="border-t border-[#2a2040] bg-[#0d0b16]">
          <div className="max-w-4xl mx-auto px-6 py-14">
            <p className="text-center text-xs text-[#5a4a7a] uppercase tracking-widest mb-8">What our customers say</p>
            <div className="grid md:grid-cols-2 gap-6">
              {TESTIMONIALS.map((t) => (
                <div key={t.name} className="onyx-card p-6 rounded-xl border border-[#2a2040]" style={{ background: "#14121e" }}>
                  <svg className="w-6 h-6 text-[#a078d0] mb-3 opacity-50" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                  <p className="text-sm text-[#c0b0d8] leading-relaxed mb-4">"{t.quote}"</p>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="text-xs text-[#8a7a9a]">{t.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
